import { useState } from "react"

import { Button } from "@coval/ui"
import {
  Node,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from "@tiptap/react"
import { GripVertical } from "lucide-react"

import { notebookMediaUrl, notebookVideoUrl } from "../utils/notebookMedia"

function VideoNode({
  node,
  editor,
  selected,
  updateAttributes,
}: NodeViewProps) {
  const [failedSource, setFailedSource] = useState<string>()
  const src = notebookVideoUrl(node.attrs.src)
  const poster = notebookMediaUrl(node.attrs.poster)
  const captions = notebookMediaUrl(node.attrs.captions)
  const label = String(node.attrs.title || "Video")
  return (
    <NodeViewWrapper
      className="group/canvas-figure relative my-4"
      contentEditable={false}
    >
      {editor.isEditable && selected && (
        <div
          className="absolute -top-7 left-0 z-10 flex items-center gap-2 rounded border border-coval-border bg-surface-default px-1 text-coval-text"
          data-drag-handle
        >
          <Button icon variant="ghost" tooltip="Drag to move">
            <GripVertical />
          </Button>
          <input
            type="range"
            className="accent-coval-text"
            aria-label="Video width"
            min={10}
            max={100}
            value={Number(node.attrs.width ?? 100)}
            onChange={(event) =>
              updateAttributes({ width: Number(event.target.value) })
            }
          />
        </div>
      )}
      {src ? (
        <>
          <video
            key={src}
            src={src}
            poster={poster}
            controls
            playsInline
            preload="metadata"
            crossOrigin={captions ? "anonymous" : undefined}
            loop={node.attrs.loop === true}
            aria-label={label}
            style={{
              width: `${Number(node.attrs.width ?? 100)}%`,
              maxHeight: "70vh",
            }}
            onError={() => setFailedSource(src)}
          >
            <track
              kind="captions"
              src={captions}
              srcLang="en"
              label="English"
              default
            />
          </video>
          {failedSource === src && (
            <p role="status">
              Video could not load. Check that the link is public and points to
              a playable video.{" "}
              <a
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                referrerPolicy="no-referrer"
              >
                Open original video
              </a>
            </p>
          )}
        </>
      ) : (
        <p role="status">
          Video unavailable: a valid HTTPS source is required.
        </p>
      )}
    </NodeViewWrapper>
  )
}

export const NotebookVideo = Node.create({
  name: "video",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes: () => ({
    src: { default: null },
    poster: { default: null },
    title: { default: null },
    captions: { default: null },
    width: { default: 100 },
    loop: {
      default: false,
      parseHTML: (element) => element.hasAttribute("loop"),
      renderHTML: (attrs) => (attrs.loop ? { loop: "" } : {}),
    },
  }),
  parseHTML: () => [
    {
      tag: "video[src]",
      getAttrs: (element) =>
        notebookVideoUrl(element.getAttribute("src")) ? null : false,
    },
  ],
  renderHTML: ({ HTMLAttributes }) => [
    "video",
    {
      ...HTMLAttributes,
      src: notebookVideoUrl(HTMLAttributes.src),
      poster: notebookMediaUrl(HTMLAttributes.poster),
      controls: "",
      playsinline: "",
      preload: "metadata",
    },
  ],
  addNodeView: () => ReactNodeViewRenderer(VideoNode),
})
